import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ds';
import { useCan } from '@/hooks/useCan';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { cn } from '@/utils/cn';
import type { PlanFeatureKey } from '@/api/billing.api';
import { PLAN_FEATURE_ICONS, upgradePath } from './plan-feature-meta';

/**
 * What a clinic sees in place of a feature its plan does not include.
 *
 * Sells rather than scolds: what the feature does for the clinic, three
 * concrete wins, and one way forward. People who cannot change the plan are
 * told who can, instead of being shown a button that would refuse them.
 */
export function PlanFeatureLocked({
  feature,
  compact = false,
  onNavigate,
}: {
  feature: PlanFeatureKey;
  /** Inside a dialog: no outer card, tighter spacing. */
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canView } = useCan();
  const { unlockedBy } = usePlanFeatures();
  const plan = unlockedBy(feature);
  const Icon = PLAN_FEATURE_ICONS[feature];
  const points = t(`planFeatures.items.${feature}.points`, {
    returnObjects: true,
    defaultValue: [],
  }) as string[];
  const canUpgrade = canView('finance');

  const body = (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        compact ? 'gap-4' : 'gap-5 px-2 py-6 sm:px-6',
      )}
    >
      <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-soft text-brand-text">
        <Icon className="size-7" aria-hidden />
      </div>

      {plan && (
        <Badge tone="info">
          <Sparkles className="size-3.5" aria-hidden />
          {t('planFeatures.onPlan', { plan: plan.name })}
        </Badge>
      )}

      <div className="space-y-2">
        <h2 className="text-h2 text-foreground">
          {t(`planFeatures.items.${feature}.name`)}
        </h2>
        <p className="mx-auto max-w-md text-body text-secondary">
          {t(`planFeatures.items.${feature}.pitch`)}
        </p>
      </div>

      {points.length > 0 && (
        <ul className="w-full max-w-md space-y-2 text-start">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-2.5 text-body">
              <Check
                className="mt-0.5 size-4 shrink-0 text-success"
                aria-hidden
              />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}

      {canUpgrade ? (
        <Button
          variant="primary"
          onPress={() => {
            onNavigate?.();
            navigate(upgradePath(plan?.planId));
          }}
        >
          {plan
            ? t('planFeatures.upgradeTo', { plan: plan.name })
            : t('planFeatures.seePlans')}
        </Button>
      ) : (
        <p className="text-small text-secondary">
          {plan
            ? t('planFeatures.askOwner', { plan: plan.name })
            : t('planFeatures.askOwnerAny')}
        </p>
      )}
    </div>
  );

  if (compact) return body;
  return <Card className="mx-auto w-full max-w-2xl">{body}</Card>;
}
